import { z } from 'zod';
import type { TimestampLike } from './timestamp-like';

export const EngagementStatusSchema = z.enum([
  'draft',
  'sent',
  'viewed',
  'signed',
  'declined',
  'expired',
  'revoking',
  'revoked',
]);
export type EngagementStatus = z.infer<typeof EngagementStatusSchema>;

const SignerSchema = z.object({
  email: z.string(),
  name: z.string(),
  role: z.string(),
  status: z.enum(['pending', 'sent', 'viewed', 'signed', 'declined']),
  viewedAt: z.custom<TimestampLike>().nullish(),
  signedAt: z.custom<TimestampLike>().nullish(),
});

export const EngagementRecordSchema = z.object({
  id: z.string(),
  boldsignDocumentId: z.string().nullish(),
  templateId: z.string(),
  templateName: z.string(),
  status: EngagementStatusSchema,
  createdAt: z.custom<TimestampLike>().nullish(),
  createdBy: z.string().nullish(),
  sentAt: z.custom<TimestampLike>().nullish(),
  sentBy: z.string().nullish(),
  senderAttorneyId: z.string().nullish(),
  senderEmail: z.string().nullish(),
  viewedAt: z.custom<TimestampLike>().nullish(),
  signedAt: z.custom<TimestampLike>().nullish(),
  signedDocumentPath: z.string().nullish(),
  declineReason: z.string().nullish(),
  expiresAt: z.custom<TimestampLike>().nullish(),
  previousInquiryStatus: z.enum(['contacted', 'need_info', 'consulting']).nullish(),
  signers: z.array(SignerSchema).nullish(),
  resendCount: z.number().nullish(),
  complianceScore: z.number().nullish(),
  firmBrandId: z.string().nullish(),
  updatedAt: z.custom<TimestampLike>(),
  updatedBy: z.string(),
});
export type EngagementRecord = z.infer<typeof EngagementRecordSchema>;

export const EngagementTemplateSchema = z.object({
  id: z.string(),
  firmId: z.string(),
  boldsignTemplateId: z.string(),
  name: z.string(),
  practiceArea: z.string().nullish(),
  description: z.string().nullish(),
  variables: z.array(z.string()).nullish(),
  signerRoles: z.array(z.object({ role: z.string(), defaultSignerIndex: z.number() })).nullish(),
  signingOrder: z.enum(['ordered', 'parallel']).nullish(),
  complianceScore: z.number().nullish(),
  configurationStatus: z.enum(['unconfigured', 'ready']).nullish(),
  complianceStatus: z.enum(['pending', 'complete']).nullish(),
  complianceDetails: z
    .array(
      z.object({
        check: z.string(),
        passed: z.boolean(),
        detail: z.string().nullish(),
      }),
    )
    .nullish(),
  createdAt: z.custom<TimestampLike>(),
  createdBy: z.string(),
  updatedAt: z.custom<TimestampLike>(),
  updatedBy: z.string(),
});
export type EngagementTemplate = z.infer<typeof EngagementTemplateSchema>;
